import {
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
} from "chart.js";
import annotationPlugin from "chartjs-plugin-annotation";
import { useMemo } from "react";
import { Line } from "react-chartjs-2";

import useCurrency from "../../../../../hooks/useCurrency";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Title,
  Tooltip,
  Legend,
  annotationPlugin
);

const PST_TZ = "America/Los_Angeles";

const getPSTHour = (iso) => {
  const h = parseInt(
    new Date(iso).toLocaleString("en-US", {
      timeZone: PST_TZ,
      hour: "numeric",
      hour12: false,
    }),
    10
  );
  return h === 24 ? 0 : h;
};

const FULL_DAY_LABELS = Array.from(
  { length: 24 },
  (_, h) => `${String(h).padStart(2, "0")}:00`
);

const buildFullDayData = (campaign, mode) => {
  const slotData = new Array(24).fill(null);
  (campaign.timestamps || []).forEach((ts, i) => {
    const hour = getPSTHour(ts);
    const val =
      mode === "percent"
        ? campaign.usage_percentages?.[i] ?? null
        : campaign.usage_dollars?.[i] ?? null;
    slotData[hour] = val;
  });
  return slotData;
};

const buildChartOptions = (mode, symbol) => ({
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: "index", intersect: false },
  plugins: {
    legend: { display: false },
    tooltip: {
      callbacks: {
        label: (ctx) => {
          const val = ctx.parsed.y;
          if (val == null) return null;
          return mode === "percent"
            ? ` ${val.toFixed(1)}%`
            : ` ${symbol}${val.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}`;
        },
      },
    },
    annotation:
      mode === "percent"
        ? {
            annotations: {
              warningLine: {
                type: "line",
                yMin: 80,
                yMax: 80,
                borderColor: "#f59e0b",
                borderWidth: 1.5,
                borderDash: [6, 4],
                label: {
                  display: true,
                  content: "80%",
                  position: "end",
                  backgroundColor: "#fffbeb",
                  color: "#92400e",
                  font: { size: 10, weight: "600" },
                  padding: { x: 5, y: 2 },
                },
              },
              criticalLine: {
                type: "line",
                yMin: 100,
                yMax: 100,
                borderColor: "#ef4444",
                borderWidth: 1.5,
                borderDash: [6, 4],
                label: {
                  display: true,
                  content: "100%",
                  position: "end",
                  backgroundColor: "#fef2f2",
                  color: "#991b1b",
                  font: { size: 10, weight: "600" },
                  padding: { x: 5, y: 2 },
                },
              },
            },
          }
        : { annotations: {} },
  },
  scales: {
    x: {
      grid: { color: "#f1f5f9" },
      ticks: {
        color: "#94a3b8",
        font: { size: 10 },
        maxRotation: 0,
        autoSkip: true,
        maxTicksLimit: 12,
      },
    },
    y: {
      beginAtZero: true,
      ...(mode === "percent" && { max: 110 }),
      grid: { color: "#f1f5f9" },
      ticks: {
        color: "#94a3b8",
        font: { size: 10 },
        callback: (v) => (mode === "percent" ? `${v}%` : `${symbol}${v}`),
      },
    },
  },
});

export const CampaignMiniChart = ({ campaign, color, mode = "percent" }) => {
  const { marketplaceCurrencySymbol } = useCurrency();
  const data = useMemo(
    () => buildFullDayData(campaign, mode),
    [campaign, mode]
  );
  const dataset = {
    label: campaign.campaign_name,
    data,
    borderColor: color,
    backgroundColor: color + "18",
    pointBackgroundColor: (ctx) =>
      ctx.parsed?.y != null ? color : "transparent",
    pointRadius: (ctx) => (ctx.parsed?.y != null ? 5 : 0),
    pointHoverRadius: 7,
    borderWidth: 2.5,
    tension: 0.35,
    fill: true,
    spanGaps: true,
  };
  const options = useMemo(
    () => buildChartOptions(mode, marketplaceCurrencySymbol),
    [mode, marketplaceCurrencySymbol]
  );
  return (
    <Line
      data={{ labels: FULL_DAY_LABELS, datasets: [dataset] }}
      options={options}
    />
  );
};
